import type { Shop } from '@prisma/client'
import { useFetcher } from '@remix-run/react'
import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { makeShopOwnPath } from '~/helpers/routes'
import type { MediaUsage } from '~/models/media.server'

type UsageLoaderData = {
  usage: MediaUsage
}

export function useMediaDeletion(shop: Shop) {
  const { t } = useTranslation()
  const usageFetcher = useFetcher<UsageLoaderData>()
  const deleteFetcher = useFetcher()

  const [mediaToDelete, setMediaToDelete] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Track callbacks for optimistic updates
  const [pendingCallbacks, setPendingCallbacks] = useState<{
    onOptimisticUpdate: (mediaId: string) => void
    onRollback: () => void
    mediaId: string
  } | null>(null)

  const initiateDelete = useCallback(
    (mediaId: string) => {
      setMediaToDelete(mediaId)
      setDeleteError(null)

      // Load usage data
      usageFetcher.load(makeShopOwnPath(shop, `/medias/${mediaId}`))
    },
    [shop, usageFetcher]
  )

  const cancelDelete = useCallback(() => {
    setDeleteError(null)
    setMediaToDelete(null)
    setPendingCallbacks(null)
  }, [])

  const confirmDelete = useCallback(
    (onOptimisticUpdate: (mediaId: string) => void, onRollback: () => void) => {
      if (!mediaToDelete) return

      // Store callbacks for handling response
      setPendingCallbacks({
        onOptimisticUpdate,
        onRollback,
        mediaId: mediaToDelete,
      })

      // Optimistic update
      onOptimisticUpdate(mediaToDelete)

      // Submit delete request
      const formData = new FormData()
      formData.append('_intent', 'deleteMedia')

      deleteFetcher.submit(formData, {
        method: 'POST',
        action: makeShopOwnPath(shop, `/medias/${mediaToDelete}`),
      })

      // Clear the dialog state
      setMediaToDelete(null)
    },
    [mediaToDelete, shop, deleteFetcher]
  )

  // Handle delete response
  useEffect(() => {
    if (
      deleteFetcher.state === 'idle' &&
      deleteFetcher.data &&
      pendingCallbacks
    ) {
      const data = deleteFetcher.data as { success?: boolean; error?: string }

      if (!data.success) {
        // Rollback on error
        setDeleteError(data.error || t('errors.media_delete_error'))
        pendingCallbacks.onRollback()
      }

      setPendingCallbacks(null)
    }
  }, [deleteFetcher.state, deleteFetcher.data, pendingCallbacks, t])

  const isLoadingUsage = usageFetcher.state === 'loading'
  const mediaUsage = usageFetcher.data?.usage ?? null

  return {
    mediaToDelete,
    mediaUsage,
    isLoadingUsage,
    deleteError,
    initiateDelete,
    cancelDelete,
    confirmDelete,
  }
}
